import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ShoppingBag, User, Search, Menu, X } from "lucide-react";
import { useApp } from "../context/AppContext";
import { getHomepageCache, fetchHomepage } from "../lib/homepage";
import { LeTopBar } from "./Limited";
import { useLimited } from "../lib/limited";
import { useFamilies } from "../lib/families";

const navLinks = [
  { to: "/", label: "Accueil" },
  { to: "/cookies", label: "Cookies" },
  { to: "/brownies", label: "Brownies" },
  { to: "/cupcookies", label: "Cupcookies" },
  { to: "/composer-ma-box", label: "Box" },
  { to: "/evenements", label: "Événements" },
  { to: "/a-propos", label: "À propos" },
];

function Group({ infos }) {
  const rep = [...infos, ...infos, ...infos, ...infos];
  return (<>{rep.map((x, i) => (<React.Fragment key={i}><span>{x}</span><span className="cs-hd-sep" aria-hidden="true">•</span></React.Fragment>))}</>);
}

export default function Header() {
  const { cartCount, user, openCart } = useApp();
  const [open, setOpen] = useState(false);
  const [bar, setBar] = useState(getHomepageCache);
  const loc = useLocation();
  const nav = useNavigate();

  useEffect(() => { fetchHomepage().then(setBar).catch(() => {}); }, []);
  const custom = ["bar_info1", "bar_info2", "bar_info3"].map((k) => String(bar?.[k] || "").trim());
  const barInfos = custom.some(Boolean) ? custom.filter(Boolean) : ["Retrait à Palaiseau", "Point relais dès 4,10 €", "Livraison à domicile dès 7,95 €"];
  const { live: leLive, loaded: leLoaded } = useLimited();
  const leOff = leLoaded && !leLive.length;
  const [fams] = useFamilies();
  const base = [...navLinks.slice(0, 4), ...fams.filter((f) => f.in_menu !== false).map((f) => ({ to: `/${f.slug}`, label: f.name })), ...navLinks.slice(4)];
  const at = base.findIndex((l) => l.to === "/composer-ma-box") + 1;
  const links = bar?.limited_visible === false || bar?.le_menu_on === false ? base : [...base.slice(0, at), { to: "/editions-limitees", label: "Éditions limitées", le: true }, ...base.slice(at)];

  useEffect(() => { setOpen(false); }, [loc.pathname]);
  useEffect(() => { document.body.style.overflow = open ? "hidden" : ""; return () => { document.body.style.overflow = ""; }; }, [open]);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <>
    <LeTopBar />
    <div className="cs-hd">
      <style>{CS_HEADER_CSS}</style>
      <div className="cs-hd-bar"><div className="cs-hd-marquee" aria-label="Infos pratiques">
        <Group infos={barInfos} />
      </div></div>
      <header className="cs-hd-main">
        <div className="cs-hd-wrap cs-hd-row">
          <Link to="/" data-testid="logo-link" className="cs-hd-logo"><img decoding="async" src="/static/home/logo-line-orange.webp" width="400" height="46" alt="Crunchy Sweet" /></Link>
          <nav className="cs-hd-nav" aria-label="Navigation principale">
            {links.map((l) => (
              <Link key={l.to} to={l.to} data-testid={`nav-${l.label.toLowerCase().replace(/\s|é/g, "-")}`} className={`${loc.pathname === l.to ? "active" : ""}${l.le ? " nav-le" : ""}${l.le && leOff ? " off" : ""}`}>{l.label}</Link>
            ))}
          </nav>
          <div className="cs-hd-actions">
            <button data-testid="search-btn" aria-label="Rechercher" onClick={() => nav("/cookies")} className="cs-hd-ic"><Search size={19} /></button>
            <Link to={user ? "/compte" : "/connexion"} data-testid="account-btn" aria-label="Mon compte" className="cs-hd-ic"><User size={19} /></Link>
            <button type="button" onClick={openCart} data-testid="cart-btn" className="cs-hd-cart" aria-label={`Panier ${cartCount} article${cartCount > 1 ? "s" : ""}`}>
              <ShoppingBag size={18} /><span className="cs-hd-cart-lbl">Panier</span>
              <span className="cs-hd-count">{cartCount}</span>
            </button>
            <button data-testid="mobile-menu-toggle" className="cs-hd-burger" aria-label="Ouvrir le menu" aria-expanded={open} onClick={() => setOpen(true)}><Menu size={20} /></button>
          </div>
        </div>
      </header>

      {open && (
        <div className="cs-hd-mmenu" data-testid="mobile-menu">
          <div className="cs-hd-mmenu-head">
            <Link to="/" className="cs-hd-logo cream"><img loading="lazy" decoding="async" src="/static/home/logo-line-cream.webp" width="480" height="55" alt="Crunchy Sweet" /></Link>
            <button className="cs-hd-mx" data-testid="mobile-menu-close" aria-label="Fermer le menu" onClick={() => setOpen(false)}><X size={22} /></button>
          </div>
          <nav className="cs-hd-mnav" aria-label="Menu mobile">
            {links.map((l) => (<Link key={l.to} to={l.to} className={l.le && leOff ? "off" : ""}>{l.label}</Link>))}
          </nav>
          <div className="cs-hd-mfoot">
            <Link to="/offrir">Offrir Crunchy Sweet</Link>
            <Link to={user ? "/compte" : "/connexion"}>Mon compte</Link>
            <Link to="/suivi">Suivre ma commande</Link>
            <a href="https://www.instagram.com/crunchysweet.fr/" target="_blank" rel="noreferrer">Instagram @crunchysweet.fr</a>
          </div>
        </div>
      )}
    </div>
    </>
  );
}

const CS_HEADER_CSS = `
.cs-hd{--orange:#C6531A;--orange-deep:#A8410F;--orange-ink:#B44A16;--paper:#FFF8F1;--cocoa:#2B1A12;--line:#E7D3C2;--display:'Bricolage Grotesque',ui-sans-serif,system-ui,sans-serif;font-family:var(--display);position:sticky;top:0;z-index:50}
.cs-hd-bar{background:var(--cocoa);color:var(--paper);font-size:14px;overflow:hidden}
.cs-hd-wrap{max-width:1320px;margin:0 auto;padding:0 20px}
.cs-hd-marquee{display:flex;align-items:center;white-space:nowrap;width:max-content;padding-block:10px;animation:cshdscroll 36s linear infinite}
.cs-hd-bar:hover .cs-hd-marquee{animation-play-state:paused}
.cs-hd-sep{padding:0 20px;color:#F4A06C}
@keyframes cshdscroll{from{transform:translateX(0)}to{transform:translateX(-50%)}}
@media(prefers-reduced-motion:reduce){.cs-hd-marquee{animation:none;width:100%;justify-content:center;flex-wrap:wrap;padding:10px 20px}}
.cs-hd-main{position:relative;z-index:1;background:var(--paper);border-bottom:1px solid var(--line)}
.cs-hd-row{display:flex;align-items:center;justify-content:space-between;gap:12px 16px;padding-block:12px}
.cs-hd-logo{display:flex;align-items:center;text-decoration:none}
.cs-hd-logo img{height:22px;width:auto;object-fit:contain;display:block}
@media(max-width:640px){.cs-hd-logo img{height:18px}}
.cs-hd-nav{display:flex;gap:2px 24px;flex-wrap:wrap;font-weight:500}
.cs-hd-nav a{text-decoration:none;padding:10px 0;color:var(--cocoa);font-size:16px;transition:color .2s}
.cs-hd-nav a:hover,.cs-hd-nav a.active{color:var(--orange-ink)}
.cs-hd-nav a.nav-le{color:var(--orange-ink);font-weight:700}
.cs-hd-nav a.off,.cs-hd-mnav a.off{opacity:.45}
@media(min-width:901px) and (max-width:1300px){.cs-hd-nav{gap:2px 13px}.cs-hd-nav a{font-size:15.5px}}
@media(min-width:901px) and (max-width:1200px){.cs-hd-nav{gap:2px 10px}.cs-hd-nav a{font-size:15px}.cs-hd-cart-lbl{display:none}}
.cs-hd-actions{display:flex;gap:8px;align-items:center}
.cs-hd-ic{position:relative;width:44px;height:44px;border-radius:999px;border:0;background:transparent;color:var(--cocoa);display:grid;place-items:center;cursor:pointer;text-decoration:none;transition:background .2s}
.cs-hd-ic:hover{background:#F3E6D3}
.cs-hd-badge{position:absolute;top:4px;right:4px;min-width:16px;height:16px;padding:0 4px;font-size:10px;display:grid;place-items:center;border-radius:999px;background:var(--orange);color:#fff}
.cs-hd-cart{min-height:46px;padding:0 18px;border-radius:999px;border:0;background:var(--orange-ink);color:#fff;display:flex;align-items:center;gap:10px;font-weight:700;font-size:16px;text-decoration:none;transition:background .2s}
.cs-hd-cart:hover{background:var(--orange-deep)}
.cs-hd-count{background:var(--paper);color:var(--orange-deep);border-radius:999px;min-width:26px;height:26px;display:inline-grid;place-items:center;font-size:14px;padding:0 6px}
.cs-hd-burger{display:none;width:46px;height:46px;border-radius:999px;border:1.5px solid var(--cocoa);background:transparent;color:var(--cocoa);align-items:center;justify-content:center;cursor:pointer}
@media(max-width:900px){.cs-hd-nav{display:none}.cs-hd-burger{display:inline-flex}.cs-hd-cart-lbl{display:none}.cs-hd-ic{display:none}}
.cs-hd-mmenu{position:fixed;inset:0;z-index:60;background:var(--orange);color:var(--paper);display:flex;flex-direction:column;padding:16px 20px 28px;overflow-y:auto}
.cs-hd-mmenu-head{display:flex;justify-content:space-between;align-items:center}
.cs-hd-logo.cream img{height:18px}
.cs-hd-mx{width:46px;height:46px;border-radius:999px;border:1.5px solid var(--paper);background:transparent;color:var(--paper);display:grid;place-items:center;cursor:pointer}
.cs-hd-mnav{display:flex;flex-direction:column;margin-top:auto;padding-block:28px}
.cs-hd-mnav a{font-size:clamp(32px,9vw,46px);font-weight:800;letter-spacing:-0.04em;line-height:1.12;padding:8px 0;border-bottom:1px solid rgba(255,248,241,.25);text-decoration:none;color:var(--paper);transition:color .2s}
.cs-hd-mnav a:hover{color:var(--cocoa)}
.cs-hd-mfoot{display:flex;flex-direction:column;gap:7px;font-size:15px;font-weight:600}
.cs-hd-mfoot a{text-decoration:none;color:var(--paper)}
`;
